import { CHAR_VOXEL_SIZE } from '../constants';

/**
 * Voxel Marines are built from 0.05 m voxels in Minecraft-style proportions (32 voxels tall).
 * Body space: x right, y up, z backward (characters face -Z), origin between the feet.
 * Each part is a separate grid (with room for the armor shell) animated as a rigid piece.
 */
export const Part = {
  Head: 0,
  Torso: 1,
  ArmL: 2,
  ArmR: 3,
  LegL: 4,
  LegR: 5,
} as const;
export type Part = (typeof Part)[keyof typeof Part];
export const PART_COUNT = 6;

export interface PartDef {
  id: Part;
  name: string;
  /** Grid origin in body voxel coords and grid size (includes the armor shell margin). */
  min: readonly [number, number, number];
  size: readonly [number, number, number];
  /** Body (skin) region in body voxel coords. */
  bodyMin: readonly [number, number, number];
  bodySize: readonly [number, number, number];
  /** Rotation pivot in body voxel coords. */
  pivot: readonly [number, number, number];
}

export const PARTS: readonly PartDef[] = [
  { id: Part.Head, name: 'head', min: [-5, 24, -5], size: [10, 9, 10], bodyMin: [-4, 24, -4], bodySize: [8, 8, 8], pivot: [0, 24, 0] },
  { id: Part.Torso, name: 'torso', min: [-4, 12, -3], size: [8, 12, 6], bodyMin: [-4, 12, -2], bodySize: [8, 12, 4], pivot: [0, 12, 0] },
  { id: Part.ArmL, name: 'armL', min: [-9, 12, -3], size: [5, 13, 6], bodyMin: [-8, 12, -2], bodySize: [4, 12, 4], pivot: [-6, 22, 0] },
  { id: Part.ArmR, name: 'armR', min: [4, 12, -3], size: [5, 13, 6], bodyMin: [4, 12, -2], bodySize: [4, 12, 4], pivot: [6, 22, 0] },
  { id: Part.LegL, name: 'legL', min: [-5, 0, -3], size: [5, 12, 6], bodyMin: [-4, 0, -2], bodySize: [4, 12, 4], pivot: [-2, 12, 0] },
  { id: Part.LegR, name: 'legR', min: [0, 0, -3], size: [5, 12, 6], bodyMin: [0, 0, -2], bodySize: [4, 12, 4], pivot: [2, 12, 0] },
];

/** Skin data: palette indices for every body voxel, part by part (x fastest, then z, then y). */
export const SKIN_OFFSETS: readonly number[] = (() => {
  const out: number[] = [];
  let o = 0;
  for (const p of PARTS) {
    out.push(o);
    o += p.bodySize[0] * p.bodySize[1] * p.bodySize[2];
  }
  out.push(o);
  return out;
})();
export const SKIN_BYTES = SKIN_OFFSETS[PART_COUNT];

export function skinIndex(part: Part, bx: number, by: number, bz: number): number {
  const p = PARTS[part];
  return SKIN_OFFSETS[part] + bx + bz * p.bodySize[0] + by * p.bodySize[0] * p.bodySize[2];
}

/** True if the body voxel is on the outer surface of its part. */
export function isSurfaceVoxel(part: Part, bx: number, by: number, bz: number): boolean {
  const [w, h, d] = PARTS[part].bodySize;
  return bx === 0 || by === 0 || bz === 0 || bx === w - 1 || by === h - 1 || bz === d - 1;
}

/**
 * Armor shell cells allowed per part (grid coords). Regions never overlap between parts in the
 * rest pose: helmets stop above the jaw, chest plates cover front/back only, shoulder pads sit
 * on top of the arms, and leg guards cover front, back and the outer side.
 */
export function isArmorCell(part: Part, gx: number, gy: number, gz: number): boolean {
  const p = PARTS[part];
  const x = gx + p.min[0];
  const y = gy + p.min[1];
  const z = gz + p.min[2];
  const inBody = (bx: number, by: number, bz: number): boolean =>
    bx >= p.bodyMin[0] && by >= p.bodyMin[1] && bz >= p.bodyMin[2] &&
    bx < p.bodyMin[0] + p.bodySize[0] && by < p.bodyMin[1] + p.bodySize[1] && bz < p.bodyMin[2] + p.bodySize[2];
  if (inBody(x, y, z)) return false;
  switch (part) {
    case Part.Head:
      // Top layer, plus sides/front/back from y >= 26.
      if (y === 32) return x >= -4 && x < 4 && z >= -4 && z < 4;
      return y >= 26 && y < 32;
    case Part.Torso:
      return z === -3 || z === 2;
    case Part.ArmL:
      return y === 24 ? x >= -8 && x < -4 : x === -9 || z === -3 || z === 2;
    case Part.ArmR:
      return y === 24 ? x >= 4 && x < 8 : x === 8 || z === -3 || z === 2;
    case Part.LegL:
      return x === -5 || z === -3 || z === 2;
    case Part.LegR:
      return x === 4 || z === -3 || z === 2;
  }
  return false;
}

// Character palette ---------------------------------------------------------------
// 0 empty, 1..63 skin paint colors, 64..127 armor colors, 128+ special.
export const CHAR_SKIN_COLORS = 63;
export const CHAR_ARMOR_BASE = 64;
export const CHAR_FRAME = 128;
export const CHAR_FRAME_DARK = 129;
export const CHAR_CORE = 130;
export const CHAR_CORE_TEAM_A = 131;
export const CHAR_CORE_TEAM_B = 132;
export const CHAR_EYE_GLOW = 133;

// Hit zones -------------------------------------------------------------------------
export const HitZone = { Head: 0, Body: 1, Legs: 2 } as const;
export type HitZone = (typeof HitZone)[keyof typeof HitZone];

export interface ZoneBox {
  zone: HitZone;
  min: readonly [number, number, number];
  max: readonly [number, number, number];
}

/** Fixed hit boxes in the player's local frame (meters). They never shrink as voxels chip away. */
export const HIT_ZONES: readonly ZoneBox[] = [
  { zone: HitZone.Head, min: [-0.22, 1.2, -0.22], max: [0.22, 1.66, 0.22] },
  { zone: HitZone.Body, min: [-0.44, 0.6, -0.17], max: [0.44, 1.26, 0.17] },
  { zone: HitZone.Legs, min: [-0.26, 0, -0.17], max: [0.26, 0.62, 0.17] },
];

export const ZONE_MULTIPLIER: Record<HitZone, number> = {
  [HitZone.Head]: 1.75,
  [HitZone.Body]: 1,
  [HitZone.Legs]: 0.8,
};

/** Maps a local-frame hit point (meters) to the nearest part and grid voxel for chipping. */
export function locateHitVoxel(lx: number, ly: number, lz: number): { part: Part; gx: number; gy: number; gz: number } {
  const bx = lx / CHAR_VOXEL_SIZE;
  const by = ly / CHAR_VOXEL_SIZE;
  const bz = lz / CHAR_VOXEL_SIZE;
  let best: Part = Part.Torso;
  let bestD = Infinity;
  for (const p of PARTS) {
    const cx = Math.min(Math.max(bx, p.min[0]), p.min[0] + p.size[0] - 0.001);
    const cy = Math.min(Math.max(by, p.min[1]), p.min[1] + p.size[1] - 0.001);
    const cz = Math.min(Math.max(bz, p.min[2]), p.min[2] + p.size[2] - 0.001);
    const d = (cx - bx) ** 2 + (cy - by) ** 2 + (cz - bz) ** 2;
    if (d < bestD) {
      bestD = d;
      best = p.id;
    }
  }
  const p = PARTS[best];
  const clampi = (v: number, lo: number, n: number): number => Math.min(Math.max(Math.floor(v - lo), 0), n - 1);
  return { part: best, gx: clampi(bx, p.min[0], p.size[0]), gy: clampi(by, p.min[1], p.size[1]), gz: clampi(bz, p.min[2], p.size[2]) };
}
